import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {FieldType, MemberAddeditComponent, MemberAddeditDialogModel} from '../../brokeragency/member-addedit/member-addedit.component';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { VendorProduct, VendorProductCompletedTransferLog, VendorProductsService } from '../../../services/vendorproducts/vendorproducts.service';


@Component({
    selector: 'app-vendor-completed-transfers-page',
    templateUrl: './vendor-completed-transfers-page.component.html',
    styleUrls: ['./vendor-completed-transfers-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VendorCompletedTransfersPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('scrollContainer') private scrollContainer?: ElementRef<HTMLElement>;

  public limit = 25;
  public page = 0;
  public searchValue = '';
  public endOfList = false;
  public loading = false;

  protected allColumnNames = ['createdAt', 'vendorProductName', 'direction', 'type', 'metadata', 'actionmenu'];
  protected items = new MatTableDataSource<any>();

  public loadError = '';
  private searchGeneration = 0;
  private activeSearchValue = '';
  private destroyed = false;

  private readonly searchDebounceMs = 300;
  private readonly searchValueChanges = new Subject<string>();
  private readonly searchSubscription: Subscription;
  private addEditFields = [
    {name: 'createdAt', label: 'Created at', fieldType: FieldType.F_STRING, readOnly: true},
    {name: 'vendorProductName', label: 'Vendor product name', fieldType: FieldType.F_STRING, readOnly: true},
    {name: 'direction', label: 'Direction', fieldType: FieldType.F_STRING, readOnly: true},
    {name: 'type', label: 'Type', fieldType: FieldType.F_STRING, readOnly: true},
    {name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON, readOnly: true},
  ];
  private vendorProducts: Array<VendorProduct> = [];

  constructor(
    private dialog: MatDialog,
    private vendorProductsService: VendorProductsService,
  ) {
    this.searchSubscription = this.searchValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe(() => this.searchItems());
  }

  ngOnInit(): void {
    this.vendorProductsService.getAllVendorProducts().then((result: Array<VendorProduct>) => {
      this.vendorProducts = result;
      if (!this.destroyed) {
        this.searchItems();
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.loadMoreIfViewportNotFilled());
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.searchGeneration += 1;
    this.searchSubscription.unsubscribe();
  }

  addVendorProductDataToCompletedTransferLogs(vendorCompletedTransferLogs: VendorProductCompletedTransferLog[]): void {
    for (const logItem of vendorCompletedTransferLogs) {
      const thisVendorProduct = this.vendorProducts.find(vendorProduct => vendorProduct.id === logItem.vendorProductId);
      if (thisVendorProduct) {
        logItem.vendorProductName = `${thisVendorProduct.vendorName} ${thisVendorProduct.productName}`;
      }
    }
  }

  async searchItems(): Promise<void> {
    await this.loadItems(true);
  }

  queueFilter(value: string): void {
    this.searchValueChanges.next((value || '').toString());
  }

  clearSearch(): void {
    this.searchValue = '';
    this.queueFilter('');
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;

    if (scrollTop + offsetHeight >= scrollHeight - 150) {
      this.loadItems();
    }
  }

  stringify(jsonObject: Record<string, any>): string {
    return JSON.stringify(jsonObject);
  }

  viewItem(item: any): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(item, this.addEditFields, 'Vendor completed transfer')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        console.log('dialog result on closing', dialogResult);
      }
    });
  }

  private async loadItems(clear = false): Promise<void> {
    if (this.destroyed) {
      return;
    }
    if (clear) {
      this.searchGeneration += 1;
      this.activeSearchValue = this.searchValue;
      this.page = 0;
      this.endOfList = false;
      this.items.data = [];
    }

    if (!clear && (this.loading || this.endOfList)) {
      return;
    }

    const generation = this.searchGeneration;
    this.loading = true;
    this.loadError = '';
    try {
      const vendorCompletedTransferLogs = await this.vendorProductsService.getVendorTransferLogs(
        this.activeSearchValue,
        this.page,
        this.limit,
      );

      if (generation !== this.searchGeneration || this.destroyed) {
        return;
      }
      this.addVendorProductDataToCompletedTransferLogs(vendorCompletedTransferLogs);

      if (!vendorCompletedTransferLogs || vendorCompletedTransferLogs.length === 0) {
        this.endOfList = true;
        return;
      }

      this.items.data = clear
        ? vendorCompletedTransferLogs
        : this.items.data.concat(vendorCompletedTransferLogs);
      this.page += 1;
      this.endOfList = vendorCompletedTransferLogs.length < this.limit;
      this.items.sort = this.sort;
    } catch (error) {
      if (generation === this.searchGeneration && !this.destroyed) {
        this.loadError = 'Unable to load vendor completed transfers. Please try again.';
      }
    } finally {
      if (generation === this.searchGeneration && !this.destroyed) {
        this.loading = false;
        if (!this.loadError) {
          this.loadMoreIfViewportNotFilled();
        }
      }
    }
  }

  private loadMoreIfViewportNotFilled(): void {
    if (this.loading || this.endOfList) {
      return;
    }

    const container = this.scrollContainer?.nativeElement;
    if (!container) {
      return;
    }

    const hasVerticalScroll = container.scrollHeight > container.clientHeight;
    const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 150;

    if (!hasVerticalScroll || nearBottom) {
      this.loadItems();
    }
  }
}
